import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppMode } from '../contexts/AppModeContext';
import { useCanvasData } from '../contexts/CanvasDataContext';
import { getCanvasUsers } from '../services/canvasUsersService';
import { MOCK_USER } from '../data/mockData';

/**
 * The one place that decides "whose data is on screen" (task 5.8).
 *
 * Moved out of Sidebar.jsx, where it was a private closure, so the sidebar and
 * the header read the same answer instead of two resolvers that can drift.
 *
 * `name` keeps the sidebar's historical fallback chain (first child, 'Canvas
 * User', 'Select Canvas User', `Canvas user <id>`) so the sidebar's text is
 * unchanged. `resolved` is the part the header relies on: it is true ONLY when
 * `name` came from a real match -- a stored selectedUser, an id found in the
 * mode's child list, or demo's MOCK_USER. Every fallback string is
 * `resolved: false`, and a consumer that must not guess (the header) shows
 * nothing in that case.
 */

export interface SelectedChildName {
  name: string;
  resolved: boolean;
  /** The matched child's id when `resolved`, else null. */
  userId: string | null;
  /** Offline mode's child index (`id`, `name`); empty in every other mode. */
  offlineChildren: OfflineChild[];
}

export interface OfflineChild {
  id: string;
  /** Display text; the 'Canvas user <id>' placeholder when there is no label. */
  name: string;
  /** True only when the index row carries a real label. */
  hasLabel: boolean;
}

const findById = (users: any, id: unknown) => {
  if (id === null || id === undefined || !Array.isArray(users)) return undefined;
  return users.find((user) => user && user.id !== null && user.id !== undefined
    && String(user.id) === String(id));
};

export function useSelectedChildName(): SelectedChildName {
  const { availableUsers } = useAuth();
  const {
    coursesMode,
    guestAvailableUsers,
    getActiveCredentials,
    pendingUserSelection,
  } = useAppMode();
  // The session's Canvas user in every mode, offline included (task 6.0t).
  const { userId: sessionUserId } = useCanvasData();

  const isOfflineMode = coursesMode === 'offline';

  // Task 6.0s. Offline mode has no live observees call to fill
  // `availableUsers`; the 6.0f child index is the source instead.
  const [offlineChildren, setOfflineChildren] = useState<OfflineChild[]>([]);

  useEffect(() => {
    if (!isOfflineMode) {
      setOfflineChildren([]);
      return undefined;
    }

    let cancelled = false;
    getCanvasUsers().then((users: any[]) => {
      if (cancelled) return;
      setOfflineChildren(users.map((user) => ({
        id: String(user.canvasUserId),
        name: user.label || `Canvas user ${user.canvasUserId}`,
        hasLabel: !!user.label,
      })));
    });
    return () => {
      cancelled = true;
    };
  }, [isOfflineMode]);

  const resolve = (): Omit<SelectedChildName, 'offlineChildren'> => {
    if (coursesMode === 'demo') {
      return { name: MOCK_USER.name, resolved: true, userId: String(MOCK_USER.id) };
    }

    if (isOfflineMode) {
      const current = offlineChildren.find((child) => child.id === String(sessionUserId));
      // An index row with no label shows the sidebar's 'Canvas user <id>'
      // placeholder, which is a raw id, not a name: resolved only with a label.
      if (current?.hasLabel) {
        return { name: current.name, resolved: true, userId: current.id };
      }
      return {
        name: current?.name || (sessionUserId ? `Canvas user ${sessionUserId}` : 'Canvas User'),
        resolved: false,
        userId: null,
      };
    }

    const credentials = getActiveCredentials();
    const isGuest = coursesMode === 'guest';

    // A selection that has not been made yet names nobody.
    if (pendingUserSelection) {
      return { name: isGuest ? 'Select Canvas User' : fallbackName(availableUsers), resolved: false, userId: null };
    }

    if (credentials?.selectedUser) {
      const selected: any = credentials.selectedUser;
      return {
        name: selected.name,
        resolved: !!selected.name,
        userId: selected.name
          ? String(selected.id ?? credentials.userId ?? '') || null
          : null,
      };
    }

    const users = isGuest ? guestAvailableUsers : availableUsers;
    if (credentials?.userId && Array.isArray(users) && users.length > 0) {
      const match = findById(users, credentials.userId);
      if (match) {
        return { name: match.name, resolved: !!match.name, userId: match.name ? String(match.id) : null };
      }
    }

    return {
      name: isGuest ? 'Select Canvas User' : fallbackName(availableUsers),
      resolved: false,
      userId: null,
    };
  };

  return { ...resolve(), offlineChildren };
}

// The live-mode sidebar fallback: the first child, else a generic label.
// Never `resolved` -- it is a guess.
function fallbackName(users: any): string {
  if (Array.isArray(users) && users.length > 0) {
    return users[0].name;
  }
  return 'Canvas User';
}
